import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  LocalShipping,
  Inventory,
  LocationOn,
  CalendarToday,
  TrendingUp,
  TrendingDown,
  Info,
  OpenInFull
} from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { styled } from '@mui/material/styles';
import moment from 'moment-timezone';

const ChartCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& .MuiCardContent-root': {
    padding: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: theme.spacing(1.5)
    }
  }
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2)
    }
  }
}));

const PalletScoreCard = ({ shipments, setExpandedChart, expandedChart }) => {
  const [sortConfig, setSortConfig] = useState({
    field: 'deliveryDate',
    direction: 'desc'
  });

  console.log(shipments, 'shipments');

  const [filterBy, setFilterBy] = useState('all'); // 'all', 'last7', 'last30', 'last90'

  // Filter shipments by date range
  const getFilteredShipments = () => {
    if (filterBy === 'all') return shipments;

    const now = moment();
    let daysToSubtract = 0;

    switch (filterBy) {
      case 'last7':
        daysToSubtract = 7;
        break;
      case 'last30':
        daysToSubtract = 30;
        break;
      case 'last90':
        daysToSubtract = 90;
        break;
      default:
        return shipments;
    }

    const cutoffDate = now.subtract(daysToSubtract, 'days').startOf('day');

    return shipments.filter(shipment => {
      if (!shipment.actualDeliveryDate) return false;
      const deliveryDate = moment(shipment.actualDeliveryDate);
      return deliveryDate.isSameOrAfter(cutoffDate);
    });
  };

  const filteredShipments = getFilteredShipments();

  // Calculate statistics
  const statistics = useMemo(() => {
    const deliveredShipments = filteredShipments.filter(
      s => s.shipmentStatus === 'Delivered' && s.totalUnits > 0
    );

    const totalPallets = deliveredShipments.reduce(
      (sum, s) => sum + (Number(s.totalUnits) || 0),
      0
    );

    const totalShipments = deliveredShipments.length;

    const uniqueCarriers = new Set(
      deliveredShipments.map(s => s.carrierName).filter(Boolean)
    ).size;

    const avgPalletsPerShipment =
      totalShipments > 0 ? (totalPallets / totalShipments).toFixed(1) : 0;

    // Calculate pallets by carrier
    const palletsByCarrier = {};
    const shipmentsByCarrier = {};

    deliveredShipments.forEach(shipment => {
      const carrier = shipment.carrierName || 'Unknown';
      const pallets = Number(shipment.totalUnits) || 0;

      if (!palletsByCarrier[carrier]) {
        palletsByCarrier[carrier] = 0;
        shipmentsByCarrier[carrier] = 0;
      }

      palletsByCarrier[carrier] += pallets;
      shipmentsByCarrier[carrier] += 1;
    });

    // Calculate pallets by location (origin city)
    const palletsByLocation = {};
    deliveredShipments.forEach(shipment => {
      const location = shipment.originCity || 'Unknown';
      const pallets = Number(shipment.totalUnits) || 0;

      if (!palletsByLocation[location]) {
        palletsByLocation[location] = 0;
      }

      palletsByLocation[location] += pallets;
    });

    // Calculate daily pallet deliveries
    const dailyPallets = {};
    deliveredShipments.forEach(shipment => {
      if (shipment.actualDeliveryDate) {
        const date = moment(shipment.actualDeliveryDate).format('YYYY-MM-DD');
        const pallets = Number(shipment.totalUnits) || 0;

        if (!dailyPallets[date]) {
          dailyPallets[date] = 0;
        }

        dailyPallets[date] += pallets;
      }
    });

    // Get top 5 carriers by pallets
    const topCarriers = Object.entries(palletsByCarrier)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([carrier, pallets]) => ({
        carrier,
        pallets,
        shipments: shipmentsByCarrier[carrier] || 0
      }));

    // Get top 5 locations by pallets
    const topLocations = Object.entries(palletsByLocation)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([location, pallets]) => ({
        location,
        pallets
      }));

    // Prepare daily chart data (last 30 days)
    const last30Days = [];
    const dailyData = [];

    for (let i = 29; i >= 0; i--) {
      const date = moment().subtract(i, 'days').format('YYYY-MM-DD');
      last30Days.push(moment(date).format('MMM DD'));
      dailyData.push(dailyPallets[date] || 0);
    }

    return {
      totalPallets,
      totalShipments,
      uniqueCarriers,
      avgPalletsPerShipment,
      topCarriers,
      topLocations,
      dailyChartData: {
        dates: last30Days,
        pallets: dailyData
      },
      palletsByCarrier: {
        carriers: Object.keys(palletsByCarrier),
        pallets: Object.values(palletsByCarrier)
      }
    };
  }, [filteredShipments]);

  // Prepare data for carrier chart
  const carrierChartData = useMemo(() => {
    const sortedCarriers = Object.entries(
      statistics.palletsByCarrier.pallets.length > 0
        ? statistics.palletsByCarrier.carriers.map((carrier, idx) => ({
            carrier,
            pallets: statistics.palletsByCarrier.pallets[idx]
          }))
        : []
    ).sort((a, b) => b.pallets - a.pallets);

    return {
      carriers: sortedCarriers.map(item => item.carrier),
      pallets: sortedCarriers.map(item => item.pallets)
    };
  }, [statistics]);

  // Handle sort for table
  const handleSort = field => {
    setSortConfig(prev => ({
      field,
      direction:
        prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sorted pallet delivery data for table
  const getSortedPalletDeliveries = () => {
    const deliveries = filteredShipments
      .filter(s => s.shipmentStatus === 'Delivered' && s.totalUnits > 0)
      .map(s => ({
        id: s.id,
        deliveryDate: s.actualDeliveryDate,
        carrier: s.carrierName || 'Unknown',
        location:
          `${s.originCity || ''}, ${s.originState || ''}`.replace(
            /^, |, $/g,
            ''
          ) || 'Unknown',
        pallets: Number(s.totalUnits) || 0,
        destination:
          `${s.destinationCity || ''}, ${s.destinationState || ''}`.replace(
            /^, |, $/g,
            ''
          ) || 'Unknown',
        proNumber: s.proNumber || '-',
        bolNumber: s.bolNumber || '-'
      }));

    return deliveries.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.field) {
        case 'deliveryDate':
          aValue = a.deliveryDate ? new Date(a.deliveryDate) : new Date(0);
          bValue = b.deliveryDate ? new Date(b.deliveryDate) : new Date(0);
          break;
        case 'carrier':
          aValue = a.carrier;
          bValue = b.carrier;
          break;
        case 'location':
          aValue = a.location;
          bValue = b.location;
          break;
        case 'pallets':
          aValue = a.pallets;
          bValue = b.pallets;
          break;
        case 'destination':
          aValue = a.destination;
          bValue = b.destination;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const palletDeliveries = getSortedPalletDeliveries();

  // Format number with commas
  const formatNumber = num => {
    return num.toLocaleString();
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Filter Chips */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant='subtitle2' sx={{ mr: 1 }}>
          Time Range:
        </Typography>
        <Chip
          label='All Time'
          onClick={() => setFilterBy('all')}
          color={filterBy === 'all' ? 'primary' : 'default'}
          variant={filterBy === 'all' ? 'filled' : 'outlined'}
          size='small'
        />
        <Chip
          label='Last 7 Days'
          onClick={() => setFilterBy('last7')}
          color={filterBy === 'last7' ? 'primary' : 'default'}
          variant={filterBy === 'last7' ? 'filled' : 'outlined'}
          size='small'
        />
        <Chip
          label='Last 30 Days'
          onClick={() => setFilterBy('last30')}
          color={filterBy === 'last30' ? 'primary' : 'default'}
          variant={filterBy === 'last30' ? 'filled' : 'outlined'}
          size='small'
        />
        <Chip
          label='Last 90 Days'
          onClick={() => setFilterBy('last90')}
          color={filterBy === 'last90' ? 'primary' : 'default'}
          variant={filterBy === 'last90' ? 'filled' : 'outlined'}
          size='small'
        />
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Inventory sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Total Pallets Delivered
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {formatNumber(statistics.totalPallets)}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Across {statistics.totalShipments} shipments
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocalShipping sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Unique Carriers
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {statistics.uniqueCarriers}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Active carriers
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Avg Pallets/Shipment
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {statistics.avgPalletsPerShipment}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Per delivery
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOn sx={{ mr: 1 }} />
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Active Locations
                </Typography>
              </Box>
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {statistics.topLocations.length}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.7 }}>
                Top shipping origins
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Daily Pallet Deliveries Chart */}
        <Grid item xs={12} md={8}>
          <ChartCard>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <CalendarToday color='primary' />
                  Daily Pallet Deliveries (Last 30 Days)
                </Typography>
                <Tooltip title='View Expanded'>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('dailyPalletDeliveries')}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ height: 300 }}>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: '100%',
                      toolbar: { show: true }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 4,
                        columnWidth: '60%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: false
                    },
                    xaxis: {
                      categories: statistics.dailyChartData.dates,
                      labels: {
                        rotate: -45,
                        rotateAlways: false,
                        style: { fontSize: '10px' }
                      },
                      tickAmount: 10
                    },
                    yaxis: {
                      title: {
                        text: 'Number of Pallets'
                      },
                      labels: {
                        formatter: val => Math.round(val)
                      }
                    },
                    colors: ['#008FFB'],
                    tooltip: {
                      y: {
                        formatter: val => val + ' pallets'
                      }
                    },
                    grid: {
                      padding: { left: 10, right: 10 }
                    }
                  }}
                  series={[
                    {
                      name: 'Pallets Delivered',
                      data: statistics.dailyChartData.pallets
                    }
                  ]}
                  type='bar'
                  height={280}
                />
              </Box>
            </CardContent>
          </ChartCard>
        </Grid>

        {/* Top 5 Carriers by Pallets Chart */}
        <Grid item xs={12} md={4}>
          <ChartCard>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <LocalShipping color='success' />
                  Top Carriers by Pallets
                </Typography>
                <Tooltip title='View Expanded'>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('topCarriersPallets')}
                    sx={{
                      bgcolor: 'success.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'success.dark' }
                    }}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ height: 300 }}>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: '100%',
                      toolbar: { show: true }
                    },
                    plotOptions: {
                      bar: {
                        horizontal: true,
                        barHeight: '50%',
                        borderRadius: 4,
                        dataLabels: { position: 'right' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val => formatNumber(val),
                      style: { fontSize: '11px', fontWeight: 'bold' }
                    },
                    xaxis: {
                      labels: {
                        formatter: val => formatNumber(val)
                      }
                    },
                    yaxis: {
                      labels: {
                        style: { fontSize: '11px' }
                      }
                    },
                    colors: ['#00E396'],
                    tooltip: {
                      y: {
                        formatter: val => formatNumber(val) + ' pallets'
                      }
                    }
                  }}
                  series={[
                    {
                      name: 'Pallets',
                      data: statistics.topCarriers.map(c => c.pallets)
                    }
                  ]}
                  labels={statistics.topCarriers.map(c => c.carrier)}
                  type='bar'
                  height={280}
                />
              </Box>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Top Locations Table */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardContent>
              <Typography
                variant='h6'
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <LocationOn color='warning' />
                Top Origin Locations by Pallets
              </Typography>
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Origin Location
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align='right'>
                        Total Pallets
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.topLocations.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.location}</TableCell>
                        <TableCell align='right'>
                          {formatNumber(item.pallets)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {statistics.topLocations.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align='center'>
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>

        {/* Top Carriers with Shipments Info */}
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardContent>
              <Typography
                variant='h6'
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <LocalShipping color='info' />
                Top Carriers Performance
              </Typography>
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Carrier</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align='right'>
                        Shipments
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align='right'>
                        Total Pallets
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align='right'>
                        Avg Pallets/Shipment
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.topCarriers.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.carrier}</TableCell>
                        <TableCell align='right'>{item.shipments}</TableCell>
                        <TableCell align='right'>
                          {formatNumber(item.pallets)}
                        </TableCell>
                        <TableCell align='right'>
                          {(item.pallets / item.shipments).toFixed(1)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {statistics.topCarriers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align='center'>
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Detailed Pallet Deliveries Table */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ChartCard>
            <CardContent>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Detailed Pallet Deliveries
              </Typography>
              <TableContainer
                component={Paper}
                variant='outlined'
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <Table stickyHeader size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'deliveryDate'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('deliveryDate')}
                        >
                          Delivery Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'carrier'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('carrier')}
                        >
                          Carrier
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'location'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('location')}
                        >
                          Origin Location
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortConfig.field === 'destination'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('destination')}
                        >
                          Destination
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align='right'>
                        <TableSortLabel
                          active={sortConfig.field === 'pallets'}
                          direction={sortConfig.direction}
                          onClick={() => handleSort('pallets')}
                        >
                          Pallets
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>PRO #</TableCell>
                      <TableCell>BOL #</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {palletDeliveries.map(delivery => (
                      <TableRow key={delivery.id} hover>
                        <TableCell>
                          {delivery.deliveryDate
                            ? moment(delivery.deliveryDate).format(
                                'MMM DD, YYYY'
                              )
                            : '-'}
                        </TableCell>
                        <TableCell>{delivery.carrier}</TableCell>
                        <TableCell>{delivery.location}</TableCell>
                        <TableCell>{delivery.destination}</TableCell>
                        <TableCell align='right' sx={{ fontWeight: 'bold' }}>
                          {delivery.pallets}
                        </TableCell>
                        <TableCell>{delivery.proNumber}</TableCell>
                        <TableCell>{delivery.bolNumber}</TableCell>
                      </TableRow>
                    ))}
                    {palletDeliveries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align='center' sx={{ py: 3 }}>
                          <Alert severity='info'>
                            No pallet delivery data available for the selected
                            time range
                          </Alert>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PalletScoreCard;
